import { axiosInstanceEntity } from './axiosInstance'

// UBQ-4542: bulk shipment completion, on top of the generic /entity/bulk-operations engine
// (operationType = SHIPMENT_COMPLETION). This module keeps a completion-shaped API for the UI and
// adapts to the generic endpoints; new operation types (e.g. transfer) get their own thin module.

const BASE = '/entity/bulk-operations'
const SHIPMENT_COMPLETION = 'SHIPMENT_COMPLETION'

export interface BulkCompleteStartRequest {
    orgId: string
    shipmentIds?: string[] // explicit selection
    scopeOrgIds?: string[] // "complete all — by organization"
}

export interface BulkCompleteStartResponse {
    batchId: string | null // operationId; null when nothing was eligible (no-op)
    total: number
    skipped: string[]
}

export interface BulkCompleteOrgCount {
    orgId: string
    orgName: string | null
    eligible: number
}

export interface BulkCompletePreviewResponse {
    total: number
    byOrg: BulkCompleteOrgCount[]
}

export type BulkCompleteStatus = 'RUNNING' | 'COMPLETED' | 'STOPPED' | 'FAILED'

export interface BulkCompleteProgress {
    batchId: string
    orgId: string
    total: number
    completed: number
    pending: number
    status: BulkCompleteStatus
    updatedAt: string | null
    pollAfterMs: number // server-recommended delay before the next poll (0 when terminal)
}

// --- generic response shapes (server) ---
interface OperationResponse {
    operationId: string | null
    total: number
    skipped: string[]
}
interface OperationProgress {
    operationId: string
    operationType: string
    orgId: string
    total: number
    completed: number
    pending: number
    status: BulkCompleteStatus
    updatedAt: string | null
    pollAfterMs?: number
}

const toStartResponse = (o: OperationResponse): BulkCompleteStartResponse => ({
    batchId: o.operationId,
    total: o.total,
    skipped: o.skipped ?? [],
})
const toProgress = (o: OperationProgress): BulkCompleteProgress => ({
    batchId: o.operationId,
    orgId: o.orgId,
    total: o.total,
    completed: o.completed,
    pending: o.pending,
    status: o.status,
    updatedAt: o.updatedAt,
    pollAfterMs: o.pollAfterMs ?? 0,
})
const toRequestBody = (p: BulkCompleteStartRequest) => ({
    operationType: SHIPMENT_COMPLETION,
    orgId: p.orgId,
    entityIds: p.shipmentIds, // explicit selection
    scopeOrgIds: p.scopeOrgIds, // by-organization
})

/** Dry-run count for the confirmation dialog. */
export const previewBulkCompletion = async (
    payload: BulkCompleteStartRequest,
): Promise<BulkCompletePreviewResponse> => {
    const { data } =
        await axiosInstanceEntity.post<BulkCompletePreviewResponse>(
            `${BASE}/preview`,
            toRequestBody(payload),
        )
    return data
}

/** Start a completion operation (async). */
export const startBulkCompletion = async (
    payload: BulkCompleteStartRequest,
): Promise<BulkCompleteStartResponse> => {
    const { data } = await axiosInstanceEntity.post<OperationResponse>(
        BASE,
        toRequestBody(payload),
    )
    return toStartResponse(data)
}

/** Current progress for an operation (cached per poll interval; visible to all viewers of the org). */
export const getBulkCompletionProgress = async (
    batchId: string,
): Promise<BulkCompleteProgress> => {
    const { data } = await axiosInstanceEntity.get<OperationProgress>(
        `${BASE}/${batchId}`,
    )
    return toProgress(data)
}

/** Active (RUNNING) operations for an org — used to re-attach the progress placeholder on page load. */
export const getActiveBulkCompletions = async (
    orgId: string,
): Promise<BulkCompleteProgress[]> => {
    const { data } = await axiosInstanceEntity.get<OperationProgress[]>(BASE, {
        params: { orgId },
    })
    return (data ?? [])
        .filter((o) => o.operationType === SHIPMENT_COMPLETION)
        .map(toProgress)
}
